import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Menu, X, Phone, Mail, MapPin, Clock, 
  Home, Building2, Heart, Star,
  Stethoscope, Brain, Dumbbell, Activity, Users,
  Zap, Shield, Award, CheckCircle, ArrowRight,
  MessageCircle, Calendar
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import drDipti from './images/dr-dipti.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Contact Info
const CONTACT = {
  phone: "9820852787",
  email: "fitsophysio@gmail.com",
  address: "Velentine Apartments, B1, Film City Rd, Malad, Azad Nagar, Gokuldham Colony, Malad East, Mumbai, Maharashtra 400097",
  whatsapp: "919820852787"
};

// Services Data
const SERVICES = [
  {
    icon: Stethoscope,
    title: "Orthopedic Physiotherapy",
    description: "Expert treatment for bone, joint, muscle, and ligament conditions including arthritis, fractures, and post-surgical rehabilitation.",
    image: "https://images.unsplash.com/photo-1768508236664-3f294aaf7d41?w=600"
  },
  {
    icon: Brain,
    title: "Neurological Rehabilitation",
    description: "Specialized care for stroke recovery, Parkinson's disease, multiple sclerosis, and other neurological conditions.",
    image: "https://images.unsplash.com/photo-1591688535138-8b1995fe8891?w=600"
  },
  {
    icon: Dumbbell,
    title: "Sports Injury Treatment",
    description: "Comprehensive rehabilitation for sports-related injuries, helping athletes return to peak performance safely.",
    image: "https://images.unsplash.com/photo-1770012905139-713758ded6ec?w=600"
  },
  {
    icon: Activity,
    title: "Post-Surgical Rehab",
    description: "Tailored recovery programs after surgeries including joint replacements, spinal procedures, and ligament repairs.",
    image: "https://images.unsplash.com/photo-1650044252595-cacd425982ff?w=600"
  },
  {
    icon: Users,
    title: "Geriatric Physiotherapy",
    description: "Gentle, effective treatments designed for elderly patients to improve mobility, balance, and quality of life.",
    image: "https://images.unsplash.com/photo-1708461859488-2a0c081ff826?w=600"
  },
  {
    icon: Home,
    title: "Home Visit Services",
    description: "Convenient physiotherapy sessions at your doorstep for patients who prefer treatment in the comfort of home.",
    image: "https://images.unsplash.com/photo-1659353888906-adb3e0041693?w=600"
  }
];

// Equipment Data
const EQUIPMENT = [
  { name: "TENS Machine", description: "Pain relief therapy" },
  { name: "Ultrasound Therapy", description: "Deep tissue healing" },
  { name: "IFT Machine", description: "Muscle stimulation" },
  { name: "Traction Unit", description: "Spinal decompression" },
  { name: "Hot/Cold Therapy", description: "Inflammation control" },
  { name: "Exercise Equipment", description: "Strength training" }
];

// Testimonials Data
const TESTIMONIALS = [
  {
    name: "Rajesh Sharma",
    rating: 5,
    review: "Dr. Dipti helped me recover from my knee surgery in record time. Her home visits made the entire process so convenient. Highly professional and caring!",
    treatment: "Post-Knee Surgery"
  },
  {
    name: "Priya Patel",
    rating: 5,
    review: "After years of chronic back pain, I finally found relief. The treatment approach is modern and effective. The affordable pricing was a bonus!",
    treatment: "Back Pain Treatment"
  },
  {
    name: "Anil Mehta",
    rating: 5,
    review: "Excellent physiotherapy for my elderly mother. Dr. Dipti's patience and expertise helped her regain mobility. Forever grateful!",
    treatment: "Geriatric Care"
  }
];

// Pricing Data
const PRICING = [
  {
    name: "Clinic Visit",
    price: "₹500",
    per: "per session",
    features: ["45-minute session", "Advanced equipment", "Expert assessment", "Progress tracking"],
    popular: false
  },
  {
    name: "Home Visit",
    price: "₹800",
    per: "per session",
    features: ["60-minute session", "No travel hassle", "Personalized care", "Flexible timing"],
    popular: true
  },
  {
    name: "Monthly Package",
    price: "₹4,500",
    per: "12 sessions",
    features: ["Best value", "Mix of clinic & home", "Priority scheduling", "Free assessment"],
    popular: false
  }
];

// Logo Component
const Logo = () => (
  <div className="flex items-center gap-2">
    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-fitso-primary flex items-center justify-center">
      <span className="text-white font-heading font-bold text-lg sm:text-xl">F</span>
    </div>
    <div>
      <span className="logo-text text-lg sm:text-xl text-fitso-primary">FitSo</span>
      <span className="logo-text text-lg sm:text-xl text-fitso-accent">Physio</span>
    </div>
  </div>
);

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Treatment", href: "#treatment" },
    { label: "Pricing", href: "#pricing" },
    { label: "Gallery", href: "#gallery" },
    { label: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass shadow-lg" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Logo />
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                // className="text-fitso-text-secondary hover:text-fitso-primary font-medium transition-colors duration-300 text-sm"
                // className="text-gray-700 hover:text-fitso-primary font-medium transition"
               className={`font-medium transition ${
  scrolled
    ? "text-gray-700 hover:text-fitso-primary"
    : "text-white drop-shadow-md"
}`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a href="#contact">
              <Button 
                className="bg-fitso-accent hover:bg-fitso-accent-hover text-white rounded-full px-5 xl:px-6 py-2 font-semibold transition-all duration-300 hover:-translate-y-1"
                data-testid="nav-book-appointment-btn"
              >
                Book Appointment
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden mobile-menu-enter bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block py-3 text-fitso-text-secondary hover:text-fitso-primary font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsOpen(false)}>
              <Button className="w-full mt-4 bg-fitso-accent hover:bg-fitso-accent-hover text-white rounded-full">
                Book Appointment
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

//this below Navigation bar is backup or for refrence which is extra developed
// const Navigation = () => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [scrolled, setScrolled] = useState(false);

//   useEffect(() => {
//     const handleScroll = () => {
//       setScrolled(window.scrollY > 60);
//     };

//     window.addEventListener("scroll", handleScroll);

//     return () => window.removeEventListener("scroll", handleScroll);
//   }, []);

//   const navLinks = [
//     { label: "Home", href: "#home" },
//     { label: "About", href: "#about" },
//     { label: "Services", href: "#services" },
//     { label: "Treatment", href: "#treatment" },
//     { label: "Pricing", href: "#pricing" },
//     { label: "Gallery", href: "#gallery" },
//     { label: "Contact", href: "#contact" }
//   ];

//   return (
//     <nav
//       className={`fixed w-full top-0 z-50 transition-all duration-300
//       ${
//         scrolled
//           ? "bg-white shadow-lg py-3"
//           : "bg-white/10 backdrop-blur-md py-4"
//       }`}
//     >
//       <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">

//         {/* Logo */}
//         <div className="flex items-center gap-2">
//           <div className="bg-fitso-primary text-white w-9 h-9 flex items-center justify-center rounded-lg font-bold">
//             F
//           </div>
//           <span className="text-xl font-semibold text-gray-800">
//             Fitso<span className="text-fitso-accent">Physio</span>
//           </span>
//         </div>

//         {/* Desktop Menu */}
//         <div className="hidden lg:flex items-center gap-8">
//           {navLinks.map((link) => (
//             <a
//               key={link.label}
//               href={link.href}
//               className="text-gray-700 hover:text-fitso-primary font-medium transition"
//             >
//               {link.label}
//             </a>
//           ))}
//         </div>

//         {/* CTA Button */}
//         <div className="hidden lg:block">
//           <a
//             href="#contact"
//             className="bg-fitso-accent text-white px-6 py-2 rounded-full font-semibold hover:bg-orange-600 transition shadow-md"
//           >
//             Book Appointment
//           </a>
//         </div>

//         {/* Mobile Button */}
//         <button
//           className="lg:hidden"
//           onClick={() => setIsOpen(!isOpen)}
//         >
//           {isOpen ? <X size={26} /> : <Menu size={26} />}
//         </button>
//       </div>

//       {/* Mobile Menu */}
//       {isOpen && (
//         <div className="lg:hidden bg-white shadow-xl">
//           <div className="flex flex-col px-6 py-4 space-y-4">
//             {navLinks.map((link) => (
//               <a
//                 key={link.label}
//                 href={link.href}
//                 className="text-gray-700 hover:text-fitso-primary font-medium"
//                 onClick={() => setIsOpen(false)}
//               >
//                 {link.label}
//               </a>
//             ))}

//             <a
//               href="#contact"
//               className="bg-fitso-accent text-white text-center py-2 rounded-full font-semibold"
//             >
//               Book Appointment
//             </a>
//           </div>
//         </div>
//       )}
//     </nav>
//   );
// };

// Hero Section - Original with background image
const HeroSection = () => (
  <section 
    id="home" 
    className="relative min-h-screen flex items-center hero-overlay"
    style={{
      backgroundImage: "url('https://images.unsplash.com/photo-1650044252595-cacd425982ff?w=1920')",
      backgroundSize: "cover",
      backgroundPosition: "center"
    }}
    data-testid="hero-section"
  >
    <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-32">
      <div className="max-w-2xl">
        <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
          <span className="text-white/90 font-medium text-xs sm:text-sm">Senior Physiotherapist • BPTh, CMT, CCC</span>
        </div>
        
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4 sm:mb-6">
          Dr. Dipti Mondal
        </h1>
        
        <p className="text-base sm:text-lg text-white/90 mb-3 sm:mb-4 font-body leading-relaxed">
          Healing That Comes To You
        </p>
        
        <p className="text-sm sm:text-base text-white/80 mb-6 sm:mb-8 font-body leading-relaxed max-w-lg">
          Expert physiotherapy care at your clinic visit or in the comfort of your home. 
          Advanced treatment techniques combined with compassionate care for your complete recovery.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <a href="#contact" className="w-full sm:w-auto">
            <Button 
              className="w-full sm:w-auto btn-primary flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4"
              data-testid="hero-book-appointment-btn"
            >
              <Calendar size={18} />
              Book Appointment
            </Button>
          </a>
          <a href={`tel:${CONTACT.phone}`} className="w-full sm:w-auto">
            <Button 
              className="w-full sm:w-auto bg-transparent border-2 border-white text-white 
              hover:bg-white hover:text-fitso-primary 
              backdrop-blur-md shadow-md 
              flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 transition-all duration-300"
              data-testid="hero-call-now-btn"
            >
              <Phone size={18} />
              Call Now
            </Button>
          </a>
        </div>

        {/* Stats */}
        <div className="flex gap-6 sm:gap-8 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/20">
          <div className="stat-item">
            <div className="stat-number">10+</div>
            <div className="stat-label">Years Exp.</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">5000+</div>
            <div className="stat-label">Patients</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">98%</div>
            <div className="stat-label">Success</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section id="about" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
        {/* Image */}
        <div className="relative">
          <div className="max-w-sm mx-auto lg:max-w-none rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={drDipti}
              alt="Dr. Dipti Mondal - Senior Physiotherapist"
              className="w-full h-full object-contain"
            />
          </div>
          {/* Floating Card */}
          <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-fitso-primary/10 rounded-full flex items-center justify-center">
                <Award className="text-fitso-primary" size={24} />
              </div>
              <div>
                <p className="font-heading font-bold text-xl sm:text-2xl text-fitso-primary">10+</p>
                <p className="text-fitso-text-secondary text-xs sm:text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="text-center lg:text-left">
          <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">About Me</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-4 sm:mb-6">
            Your Recovery Partner
          </h2>
          <p className="text-fitso-text-secondary leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
            I am Dr. Dipti Mondal, a Senior Physiotherapist with extensive experience in treating 
            a wide range of musculoskeletal, neurological, and post-surgical conditions. With qualifications 
            including BPTh, CMT, and CCC, I bring both expertise and compassion to every treatment session.
          </p>
          <p className="text-fitso-text-secondary leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
            My practice offers both clinic visits and home physiotherapy services, ensuring that 
            quality healthcare is accessible to everyone. I believe in a holistic approach that 
            combines advanced techniques with personalized care plans.
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {[
              { icon: Home, text: "Home Visits Available" },
              { icon: Building2, text: "Modern Clinic" },
              { icon: Heart, text: "Patient-Centered Care" },
              { icon: Shield, text: "Safe & Effective" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-fitso-bg-alt rounded-lg sm:rounded-xl">
                <item.icon className="text-fitso-primary flex-shrink-0" size={18} />
                <span className="text-fitso-text font-medium text-xs sm:text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section id="services" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg-alt" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="text-center mb-10 sm:mb-16">
        <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Our Services</span>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-3 sm:mb-4">
          Comprehensive Physiotherapy Care
        </h2>
        <p className="text-fitso-text-secondary max-w-2xl mx-auto text-sm sm:text-base px-4">
          From orthopedic conditions to neurological rehabilitation, we offer specialized treatments 
          tailored to your specific needs.
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {SERVICES.map((service, idx) => (
          <div
            key={idx}
            className={`service-card bg-white rounded-2xl overflow-hidden ${
              idx === 0 ? "lg:row-span-2" : ""
            }`}
            data-testid={`service-card-${idx}`}
          >
            <div className={`relative ${idx === 0 ? "h-48 lg:h-80" : "h-40 sm:h-48"}`}>
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-lg sm:rounded-xl flex items-center justify-center">
                  <service.icon className="text-fitso-primary" size={20} />
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <h3 className="font-heading text-lg sm:text-xl font-bold text-fitso-text mb-2">
                {service.title}
              </h3>
              <p className="text-fitso-text-secondary text-xs sm:text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Equipment Section
const EquipmentSection = () => (
  <section id="treatment" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg" data-testid="equipment-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        <div className="text-center lg:text-left">
          <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Our Equipment</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-4 sm:mb-6">
            Advanced Technology for Better Results
          </h2>
          <p className="text-fitso-text-secondary leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
            We use state-of-the-art physiotherapy equipment to ensure effective treatment 
            and faster recovery. Our clinic is equipped with modern machines that provide 
            pain relief, improve mobility, and accelerate healing.
          </p>

          {/* Equipment Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {EQUIPMENT.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-fitso-bg-alt hover:border-fitso-primary transition-colors duration-300"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-fitso-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="text-fitso-primary" size={18} />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-fitso-text text-xs sm:text-sm truncate">{item.name}</h4>
                  <p className="text-fitso-text-muted text-xs truncate">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image */}
        <div className="relative order-first lg:order-last">
          <div className="aspect-square max-w-sm mx-auto lg:max-w-none rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1691935071222-c008a4ccc2ca?w=800"
              alt="Physiotherapy Equipment"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Treatment Process Section
const TreatmentProcess = () => {
  const steps = [
    { num: "01", title: "Initial Assessment", desc: "Comprehensive evaluation of your condition" },
    { num: "02", title: "Custom Treatment Plan", desc: "Personalized therapy plan for your needs" },
    { num: "03", title: "Therapy Sessions", desc: "Regular sessions with advanced techniques" },
    { num: "04", title: "Progress Monitoring", desc: "Continuous tracking for optimal results" }
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-fitso-primary" data-testid="treatment-process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">How We Work</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white font-bold mt-2 sm:mt-3 mb-3 sm:mb-4">
            Your Journey to Recovery
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto text-sm sm:text-base px-4">
            We follow a systematic approach to ensure the best outcomes for our patients.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="text-center relative">
              <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-3 sm:mb-6">
                <span className="font-heading text-lg sm:text-2xl font-bold text-fitso-accent">{step.num}</span>
              </div>
              <h3 className="font-heading text-sm sm:text-lg lg:text-xl font-bold text-white mb-1 sm:mb-3">{step.title}</h3>
              <p className="text-white/70 text-xs sm:text-sm">{step.desc}</p>
              
              {idx < 3 && (
                <div className="hidden md:block absolute top-7 sm:top-10 left-[60%] w-[80%]">
                  <ArrowRight className="text-white/30" size={24} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => (
  <section className="py-16 sm:py-24 lg:py-32 bg-fitso-bg-alt" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="text-center mb-10 sm:mb-16">
        <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Testimonials</span>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-3 sm:mb-4">
          What Our Patients Say
        </h2>
        <p className="text-fitso-text-secondary max-w-2xl mx-auto text-sm sm:text-base px-4">
          Real experiences from patients who have benefited from our physiotherapy services.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {TESTIMONIALS.map((testimonial, idx) => (
          <div 
            key={idx} 
            className="testimonial-card bg-white rounded-2xl p-5 sm:p-8 shadow-lg hover-lift"
            data-testid={`testimonial-card-${idx}`}
          >
            <div className="flex gap-1 mb-3 sm:mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="fill-fitso-accent text-fitso-accent" size={16} />
              ))}
            </div>
            <p className="text-fitso-text-secondary leading-relaxed mb-4 sm:mb-6 relative z-10 text-sm sm:text-base">
              "{testimonial.review}"
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-fitso-primary rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">{testimonial.name.charAt(0)}</span>
              </div>
              <div>
                <h4 className="font-semibold text-fitso-text text-sm sm:text-base">{testimonial.name}</h4>
                <p className="text-fitso-text-muted text-xs sm:text-sm">{testimonial.treatment}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = () => (
  <section id="pricing" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="text-center mb-10 sm:mb-16">
        <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Pricing</span>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-3 sm:mb-4">
          Affordable Care for Everyone
        </h2>
        <p className="text-fitso-text-secondary max-w-2xl mx-auto text-sm sm:text-base px-4">
          Quality physiotherapy doesn't have to be expensive. Choose a plan that suits your needs.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto">
        {PRICING.map((plan, idx) => (
          <div 
            key={idx} 
            className={`bg-white rounded-2xl p-5 sm:p-8 ${plan.popular ? "pricing-popular shadow-xl sm:scale-105" : "border border-fitso-bg-alt"}`}
            data-testid={`pricing-card-${idx}`}
          >
            <h3 className="font-heading text-lg sm:text-xl font-bold text-fitso-text mb-2">{plan.name}</h3>
            <div className="mb-4 sm:mb-6">
              <span className="font-heading text-3xl sm:text-4xl font-bold text-fitso-primary">{plan.price}</span>
              <span className="text-fitso-text-muted ml-2 text-sm">{plan.per}</span>
            </div>
            <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 sm:gap-3">
                  <CheckCircle className="text-fitso-success flex-shrink-0" size={16} />
                  <span className="text-fitso-text-secondary text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            <a href="#contact">
              <Button 
                className={`w-full rounded-full py-3 ${
                  plan.popular 
                    ? "bg-fitso-accent hover:bg-fitso-accent-hover text-white" 
                    : "bg-fitso-primary hover:bg-fitso-primary-dark text-white"
                }`}
              >
                Book Now
              </Button>
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Gallery Section
const GallerySection = () => {
  const [images, setImages] = useState([]);

  // useEffect(() => {
  //   const fetchGallery = async () => {
  //     try {
  //       const response = await axios.get(`${API}/gallery`);
  //       setImages(response.data);
  //     } catch (error) {
  //       console.error("Error fetching gallery:", error);
  //     }
  //   };
  //   fetchGallery();
  // }, []);
  useEffect(() => {
  // Temporarily disabled backend call
  setImages([]);
}, []);




  const placeholderImages = [
    { title: "Clinic Interior", image_url: "https://images.unsplash.com/photo-1759691555407-3c5834e6991e?w=600" },
    { title: "Treatment Session", image_url: "https://images.unsplash.com/photo-1650044252595-cacd425982ff?w=600" },
    { title: "Modern Equipment", image_url: "https://images.unsplash.com/photo-1691935071222-c008a4ccc2ca?w=600" }
  ];

  // const displayImages = images.length > 0 ? images : placeholderImages;
  const displayImages = Array.isArray(images) && images.length > 0
  ? images
  : placeholderImages;


  return (
    <section id="gallery" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg-alt" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Gallery</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-3 sm:mb-4">
            Our Clinic & Facilities
          </h2>
          <p className="text-fitso-text-secondary max-w-2xl mx-auto text-sm sm:text-base px-4">
            Take a glimpse at our modern clinic and treatment facilities.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {displayImages.map((img, idx) => (
            <div 
              key={idx} 
              className="aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-lg hover-lift"
              data-testid={`gallery-image-${idx}`}
            >
              <img
                src={img.image_url}
                alt={img.title}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <p className="text-center text-fitso-text-muted mt-6 sm:mt-8 text-xs sm:text-sm">
            More photos coming soon! Contact us to see our facilities in person.
          </p>
        )}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    visit_type: "clinic",
    preferred_date: "",
    preferred_time: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service || "General Consultation",
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        visit_type: formData.visit_type,
        notes: formData.message
      });

      toast.success("Appointment request submitted! We'll contact you shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        visit_type: "clinic",
        preferred_date: "",
        preferred_time: "",
        message: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-32 bg-fitso-bg" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Contact Info */}
          <div className="text-center lg:text-left">
            <span className="text-fitso-accent font-semibold uppercase tracking-wider text-xs sm:text-sm">Contact Us</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-fitso-text font-bold mt-2 sm:mt-3 mb-4 sm:mb-6">
              Get In Touch
            </h2>
            <p className="text-fitso-text-secondary leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              Ready to start your recovery journey? Book an appointment or reach out for any queries. 
              We're here to help!
            </p>

            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 sm:gap-4 group justify-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-fitso-primary/10 rounded-xl flex items-center justify-center group-hover:bg-fitso-primary transition-colors duration-300 flex-shrink-0">
                  <Phone className="text-fitso-primary group-hover:text-white transition-colors duration-300" size={20} />
                </div>
                <div className="text-left">
                  <p className="text-fitso-text-muted text-xs sm:text-sm">Phone</p>
                  <p className="text-fitso-text font-semibold text-sm sm:text-base">+91 {CONTACT.phone}</p>
                </div>
              </a>

              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 sm:gap-4 group justify-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-fitso-primary/10 rounded-xl flex items-center justify-center group-hover:bg-fitso-primary transition-colors duration-300 flex-shrink-0">
                  <Mail className="text-fitso-primary group-hover:text-white transition-colors duration-300" size={20} />
                </div>
                <div className="text-left">
                  <p className="text-fitso-text-muted text-xs sm:text-sm">Email</p>
                  <p className="text-fitso-text font-semibold text-sm sm:text-base break-all">{CONTACT.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-3 sm:gap-4 justify-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-fitso-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-fitso-primary" size={20} />
                </div>
                <div className="text-left">
                  <p className="text-fitso-text-muted text-xs sm:text-sm">Address</p>
                  <p className="text-fitso-text font-semibold text-sm sm:text-base">{CONTACT.address}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 sm:gap-4 justify-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-fitso-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="text-fitso-primary" size={20} />
                </div>
                <div className="text-left">
                  <p className="text-fitso-text-muted text-xs sm:text-sm">Working Hours</p>
                  <p className="text-fitso-text font-semibold text-sm sm:text-base">Mon - Sat: 9:00 AM - 8:00 PM</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="map-container h-48 sm:h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3768.3823168913455!2d72.87013537593795!3d19.17849684874538!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7b77240c873%3A0xf0262709ea381dab!2sFitsophysio!5e0!3m2!1sen!2sus!4v1773172961058!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="FitSo Physio Location"
              />
            </div>
          </div>

          {/* Appointment Form */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 shadow-xl">
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-fitso-text mb-4 sm:mb-6">Book an Appointment</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <Label htmlFor="name" className="text-fitso-text font-medium text-sm">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-fitso-text font-medium text-sm">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Your phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-fitso-text font-medium text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary"
                  data-testid="contact-email-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <Label className="text-fitso-text font-medium text-sm">Service Required</Label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => setFormData({...formData, service: value})}
                  >
                    <SelectTrigger className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0" data-testid="contact-service-select">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="orthopedic">Orthopedic Physiotherapy</SelectItem>
                      <SelectItem value="neurological">Neurological Rehab</SelectItem>
                      <SelectItem value="sports">Sports Injury</SelectItem>
                      <SelectItem value="post-surgical">Post-Surgical Rehab</SelectItem>
                      <SelectItem value="geriatric">Geriatric Care</SelectItem>
                      <SelectItem value="general">General Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-fitso-text font-medium text-sm">Visit Type *</Label>
                  <Select 
                    value={formData.visit_type} 
                    onValueChange={(value) => setFormData({...formData, visit_type: value})}
                  >
                    <SelectTrigger className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0" data-testid="contact-visit-type-select">
                      <SelectValue placeholder="Select visit type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinic">Clinic Visit</SelectItem>
                      <SelectItem value="home">Home Visit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <Label htmlFor="date" className="text-fitso-text font-medium text-sm">Preferred Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                    required
                    className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary"
                    data-testid="contact-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="time" className="text-fitso-text font-medium text-sm">Preferred Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.preferred_time}
                    onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                    required
                    className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary"
                    data-testid="contact-time-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-fitso-text font-medium text-sm">Additional Notes</Label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your condition or any special requirements..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={3}
                  className="mt-1 sm:mt-2 bg-fitso-bg-alt border-0 focus:ring-2 focus:ring-fitso-primary resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-fitso-accent hover:bg-fitso-accent-hover text-white rounded-full py-4 sm:py-5 font-semibold text-base sm:text-lg transition-all duration-300 hover:-translate-y-1"
                data-testid="contact-submit-btn"
              >
                {loading ? "Submitting..." : "Request Appointment"}
              </Button>
            </form>

            {/* WhatsApp Alternative */}
            <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-fitso-bg-alt text-center">
              <p className="text-fitso-text-muted text-xs sm:text-sm mb-3">Or book instantly via WhatsApp</p>
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=Hi Dr. Dipti, I would like to book an appointment for physiotherapy.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base"
                data-testid="whatsapp-booking-btn"
              >
                <MessageCircle size={18} />
                Book via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-fitso-bg-dark py-12 sm:py-16" data-testid="footer-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
        {/* Brand */}
        <div className="sm:col-span-2 text-center sm:text-left">
          <div className="flex items-center gap-2 mb-4 justify-center sm:justify-start">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-fitso-accent flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg sm:text-xl">F</span>
            </div>
            <div>
              <span className="logo-text text-lg sm:text-xl text-white">FitSo</span>
              <span className="logo-text text-lg sm:text-xl text-fitso-accent">Physio</span>
            </div>
          </div>
          <p className="text-white/60 leading-relaxed mb-4 sm:mb-6 max-w-sm mx-auto sm:mx-0 text-sm sm:text-base">
            Expert physiotherapy care by Dr. Dipti Mondal. Clinic visits and home services 
            available in Mumbai.
          </p>
          {/* Social Links Placeholder */}
          <div className="flex gap-3 sm:gap-4 justify-center sm:justify-start">
            <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-fitso-accent transition-colors duration-300" data-testid="social-facebook">
              <span className="text-white text-xs sm:text-sm">FB</span>
            </a>
            <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-fitso-accent transition-colors duration-300" data-testid="social-instagram">
              <span className="text-white text-xs sm:text-sm">IG</span>
            </a>
            <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-fitso-accent transition-colors duration-300" data-testid="social-linkedin">
              <span className="text-white text-xs sm:text-sm">IN</span>
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="text-center sm:text-left">
          <h4 className="font-heading font-bold text-white mb-3 sm:mb-4 text-base sm:text-lg">Quick Links</h4>
          <ul className="space-y-2 sm:space-y-3">
            {["Home", "About", "Services", "Pricing", "Contact"].map((link) => (
              <li key={link}>
                <a href={`#${link.toLowerCase()}`} className="text-white/60 hover:text-fitso-accent transition-colors duration-300 text-sm sm:text-base">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="text-center sm:text-left">
          <h4 className="font-heading font-bold text-white mb-3 sm:mb-4 text-base sm:text-lg">Contact Info</h4>
          <ul className="space-y-2 sm:space-y-3 text-white/60 text-sm sm:text-base">
            <li className="flex items-center gap-2 justify-center sm:justify-start">
              <Phone size={14} className="text-fitso-accent flex-shrink-0" />
              <span>+91 {CONTACT.phone}</span>
            </li>
            <li className="flex items-center gap-2 justify-center sm:justify-start">
              <Mail size={14} className="text-fitso-accent flex-shrink-0" />
              <span className="break-all">{CONTACT.email}</span>
            </li>
            <li className="flex items-start gap-2 justify-center sm:justify-start">
              <MapPin size={14} className="text-fitso-accent mt-1 flex-shrink-0" />
              <span>Malad East, Mumbai</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="pt-6 sm:pt-8 border-t border-white/10 text-center">
        <p className="text-white/40 text-xs sm:text-sm">
          © {new Date().getFullYear()} FitSo Physio. All rights reserved. | Dr. Dipti Mondal - BPTh, CMT, CCC
        </p>
        <p className="text-white/40 text-xs sm:text-sm">
    Designed and Managed by{" "}
    <a
      href="https://devixelx.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 hover:text-blue-300 underline transition"
    >
      DevixelX
    </a>
  </p>
      </div>
    </div>
  </footer>
);

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${CONTACT.whatsapp}?text=Hi Dr. Dipti, I would like to inquire about physiotherapy services.`}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-16 sm:h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl whatsapp-btn hover:scale-110 transition-transform duration-300"
    data-testid="whatsapp-fab"
  >
    <MessageCircle className="text-white" size={24} />
  </a>
);

// Main App Component
function App() {
  return (
    <div className="App font-body overflow-x-hidden">
      <Toaster position="top-right" richColors />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <EquipmentSection />
      <TreatmentProcess />
      <TestimonialsSection />
      {/* <PricingSection /> */}
      <GallerySection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
